'use client';

import { PublicKey } from '@drift-labs/sdk';
import { usePathname } from 'next/navigation';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import ExchangeHistoryClient from 'src/utils/exchangeHistoryClient';
import { useEffect } from 'react';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useCurrentAuthority from './useCurrentAuthority';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';

const useDownloadRequests = () => {
	const pathname = usePathname();
	const shouldSync = needsDataRequirement(pathname ?? '', 'portfolio');
	const setAccountsStore = useDriftAccountsStore((s) => s.set);
	const isEmulationMode = useDriftStore((s) => s.isEmulatingAccount);
	const fetched = useDriftAccountsStore(
		(s) => s.downloadFiles.initialHistoryLoaded
	);
	const currentAuthority = useCurrentAuthority();
	const appEventEmitter = useAppEventEmitter();

	// Comma-separated list of pending download keys so we can detect if a new one becomes available
	const [pendingDownloadKeys, setPendingDownloadKeys] = useSyncLocalStorage(
		'pendingDownloadKeys',
		''
	);

	const getDownloadRequests = async (
		authority: PublicKey,
		skipUpdateLocalStorage?: boolean
	) => {
		if (!authority) return;

		const downloadRequestInfo = await ExchangeHistoryClient.getDownloadRequests(
			authority,
			isEmulationMode
		);

		if (!downloadRequestInfo.success) return false;

		let newAvailable = false;
		const newPendingKeyArr: string[] = [];

		if (pendingDownloadKeys) {
			const localStoragePendingKeyArr = pendingDownloadKeys.split(',');
			const availableKeys =
				downloadRequestInfo.body?.data.records.available.map(
					(record) => record.key
				);
			const pendingKeys = downloadRequestInfo.body?.data.records.pending.map(
				(record) => record.key
			);

			localStoragePendingKeyArr
				.filter((item) => !!item)
				.forEach((localPendingKey) => {
					if (
						availableKeys?.includes(localPendingKey) &&
						!pendingKeys?.includes(localPendingKey)
					) {
						newAvailable = true;
					} else if (pendingKeys?.includes(localPendingKey)) {
						newPendingKeyArr.push(localPendingKey);
					} else {
						// somehow got a mismatch between api and local storage, possible user changed wallets
						// clear the local storage in this case
					}
				});
		}

		setAccountsStore((s) => {
			s.downloadFiles = {
				initialHistoryLoaded: true,
				newAvailableDetected: newAvailable,
				records: downloadRequestInfo.body?.data.records ?? {
					pending: [],
					available: [],
				},
				counts: downloadRequestInfo.body?.data.counts ?? {
					pending: 0,
					available: 0,
				},
			};
		});

		// If we have a new available download, we emit a `downloadReady` event,
		// which is picked up by our alert notification listener.
		if (newAvailable) {
			appEventEmitter.emit('downloadReady');
		}

		if (!skipUpdateLocalStorage) {
			const newLocalStorageValue =
				newPendingKeyArr.length > 0 ? newPendingKeyArr.join(',') : '';

			if (pendingDownloadKeys !== newLocalStorageValue) {
				setPendingDownloadKeys(newLocalStorageValue);
			}
		}

		return true;
	};

	// do initial fetch
	useEffect(() => {
		if (!fetched && currentAuthority) {
			getDownloadRequests(currentAuthority, true);
		}
	}, [currentAuthority, fetched]);

	// if there are pending requests, re-fetch the records once a every 20s and notify if it comes through
	useEffect(() => {
		if (!pendingDownloadKeys || !currentAuthority || !shouldSync) return;

		getDownloadRequests(currentAuthority, true);
		const interval = setInterval(async () => {
			await getDownloadRequests(currentAuthority, false);
		}, 20000);

		return () => {
			clearInterval(interval);
		};
	}, [pendingDownloadKeys, currentAuthority, shouldSync]);
};

export default useDownloadRequests;
